'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { UserPayload } from '~/server/journey/buildUserPayload';
import type { JOURNEY_EDIT_ERROR, JourneyTransformed, PricingTier } from '~/types/Journey.types';
import { isDev } from '~/utils';
import { preserveHeaderImages } from '../preserveHeaderImages';
export type FuturePricingTiers = {
  blueprintId: string;
  cheapestAvailableDate: string | null;
  cheapestAvailableDateUpdated: string | null;
  pricingTiers: PricingTier[];
} | null;
export type UnavailableDates = {
  blueprintId: string;
  dates: string[] | null;
} | null;
export type JourneyData = {
  user: null | UserPayload;
  journeyType: null | string;
  urlPath: null | string;
  futurePricingTiers?: FuturePricingTiers | null;
  unavailableDates?: UnavailableDates | null;
};
export type TripSection = 'overview' | 'accommodation' | 'transport' | 'passenger-details' | 'payment';
export const TRIP_SECTIONS: TripSection[] = ['overview', 'accommodation', 'transport', 'payment', 'passenger-details'];
export const TRIP_SECTION_LABEL_KEYS = {
  overview: 'journey.stepper_step_overview',
  accommodation: 'journey.includes_accommodation',
  transport: 'journey.includes_transport',
  payment: 'journey.stepper_step_review_trip_details',
  'passenger-details': 'content.checkout.passengers.title'
} as const satisfies Record<TripSection, string>;
export type TripOverviewSubsection = 'description' | 'key-highlights' | 'whats-included' | 'why-byway' | 'itinerary' | 'booking-details' | 'faqs';
export type TripJourneyState = {
  journey: JourneyTransformed | null;
  journeyData: JourneyData;
  currentJourneyTotal: number | null;
  journeyEditErrors: JOURNEY_EDIT_ERROR[];
  isMobileEditPanelOpen: boolean;
  mobileEditPanelIndex: number;
  isCheckingAvailability: boolean;
  activeSection: TripSection;
  activeOverviewSubsection: TripOverviewSubsection | null;
  currentStepIndex: number;
  completedSections: TripSection[];
  expandedItineraryDay: number | null;
  expandedHighlightIndex: number | null;
};
export type TripJourneyActions = {
  setJourney: (journey: JourneyTransformed | null) => void;
  setJourneyData: (data: JourneyData) => void;
  setCurrentJourneyTotal: (price: number | null) => void;
  setJourneyEditErrors: (errors: JOURNEY_EDIT_ERROR[]) => void;
  setIsMobileEditPanelOpen: (isOpen: boolean) => void;
  setMobileEditPanelIndex: (index: number) => void;
  setIsCheckingAvailability: (isCheckingAvailability: boolean) => void;
  setActiveSection: (section: TripSection) => void;
  setActiveOverviewSubsection: (subsection: TripOverviewSubsection | null) => void;
  setCurrentStepIndex: (stepIndex: number) => void;
  markSectionComplete: (section: TripSection) => void;
  setExpandedItineraryDay: (day: number | null) => void;
  setExpandedHighlightIndex: (index: number | null) => void;
  reset: () => void;
};
type TripJourneyStore = TripJourneyState & {
  actions: TripJourneyActions;
};
const initialJourneyData: JourneyData = {
  user: null,
  journeyType: null,
  urlPath: null,
  futurePricingTiers: null,
  unavailableDates: null
};
const getInitialState = (): Omit<TripJourneyStore, 'actions'> => ({
  journey: null,
  journeyData: initialJourneyData,
  currentJourneyTotal: null,
  journeyEditErrors: [],
  isMobileEditPanelOpen: false,
  mobileEditPanelIndex: 0,
  isCheckingAvailability: false,
  activeSection: 'overview',
  activeOverviewSubsection: null,
  currentStepIndex: 0,
  completedSections: [],
  expandedItineraryDay: null,
  expandedHighlightIndex: null
});
export const useTripJourneyStore = create<TripJourneyStore>()(devtools(set => {
  const actions: TripJourneyActions = {
    setJourney: journey => set(state => ({
      journey: journey ? preserveHeaderImages(journey, state.journey) : journey,
      currentJourneyTotal: null
    })),
    setJourneyData: journeyData => set({
      journeyData
    }),
    setCurrentJourneyTotal: currentJourneyTotal => set({
      currentJourneyTotal
    }),
    setJourneyEditErrors: journeyEditErrors => set({
      journeyEditErrors
    }),
    setIsMobileEditPanelOpen: isMobileEditPanelOpen => set({
      isMobileEditPanelOpen
    }),
    setMobileEditPanelIndex: mobileEditPanelIndex => set({
      mobileEditPanelIndex
    }),
    setIsCheckingAvailability: isCheckingAvailability => set({
      isCheckingAvailability
    }),
    setActiveSection: activeSection => set({
      activeSection
    }),
    setActiveOverviewSubsection: activeOverviewSubsection => set({
      activeOverviewSubsection
    }),
    setCurrentStepIndex: currentStepIndex => set({
      currentStepIndex
    }),
    markSectionComplete: section => set(state => ({
      completedSections: state.completedSections.includes(section) ? state.completedSections : [...state.completedSections, section]
    })),
    setExpandedItineraryDay: expandedItineraryDay => set({
      expandedItineraryDay
    }),
    setExpandedHighlightIndex: expandedHighlightIndex => set({
      expandedHighlightIndex
    }),
    reset: () => set(getInitialState())
  };
  return {
    ...getInitialState(),
    actions
  };
}, {
  enabled: isDev(),
  name: 'tripJourneyStore'
}));
export const useTripJourneyDataStore = () => {
  return useTripJourneyStore(useShallow(_temp));
};
export const useTripCurrentJourney = () => {
  const journey = useTripJourneyStore(_temp2);
  if (!journey) {
    throw new Error("Journey not found in store");
  }
  return journey;
};
export const useTripCurrentJourneyTotal = () => {
  return useTripJourneyStore(_temp3);
};
export const useTripJourneyEditErrors = () => {
  return useTripJourneyStore(_temp4);
};
export const useTripIsCheckingAvailability = () => {
  return useTripJourneyStore(_temp5);
};
export const useTripMobileEditPanel = () => {
  return useTripJourneyStore(useShallow(_temp6));
};
export const useTripActiveSection = () => {
  return useTripJourneyStore(_temp7);
};
export const useTripActiveOverviewSubsection = () => {
  return useTripJourneyStore(_temp8);
};
export const useTripCurrentStepIndex = () => {
  return useTripJourneyStore(_temp9);
};
export const useTripCompletedSections = () => {
  return useTripJourneyStore(_temp10);
};
export const useTripExpandedItineraryDay = () => {
  return useTripJourneyStore(_temp11);
};
export const useTripExpandedHighlightIndex = () => {
  return useTripJourneyStore(_temp12);
};
export const useTripJourneyActions = () => {
  return useTripJourneyStore(_temp13);
};
export const useTripOverviewNav = () => {
  return useTripJourneyStore(useShallow(_temp14));
};
export const useTripJourneyNullable = () => {
  return useTripJourneyStore(_temp15);
};
export function ClearTripJourneyOnMount(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const reset = useTripJourneyStore(_temp16);
  let t1;
  let t2;
  if ($[0] !== reset) {
    t1 = () => {
      reset();
    };
    t2 = [reset];
    $[0] = reset;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== children) {
    t3 = <>{children}</>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp16(state) {
  return state.actions.reset;
}
function _temp(state) {
  return {
    user: state.journeyData.user,
    journeyType: state.journeyData.journeyType,
    urlPath: state.journeyData.urlPath,
    futurePricingTiers: state.journeyData.futurePricingTiers,
    unavailableDates: state.journeyData.unavailableDates
  };
}
function _temp2(state) {
  return state.journey;
}
function _temp3(state) {
  return state.currentJourneyTotal;
}
function _temp4(state) {
  return state.journeyEditErrors;
}
function _temp5(state) {
  return state.isCheckingAvailability;
}
function _temp6(state) {
  return {
    isMobileEditPanelOpen: state.isMobileEditPanelOpen,
    setIsMobileEditPanelOpen: state.actions.setIsMobileEditPanelOpen,
    mobileEditPanelIndex: state.mobileEditPanelIndex,
    setMobileEditPanelIndex: state.actions.setMobileEditPanelIndex
  };
}
function _temp7(state) {
  return state.activeSection;
}
function _temp8(state) {
  return state.activeOverviewSubsection;
}
function _temp9(state) {
  return state.currentStepIndex;
}
function _temp10(state) {
  return state.completedSections;
}
function _temp11(state) {
  return state.expandedItineraryDay;
}
function _temp12(state) {
  return state.expandedHighlightIndex;
}
function _temp13(state) {
  return state.actions;
}
function _temp14(state) {
  return {
    activeOverviewSubsection: state.activeOverviewSubsection,
    setActiveOverviewSubsection: state.actions.setActiveOverviewSubsection
  };
}
function _temp15(state) {
  return state.journey;
}